import axios from "axios";

// The 8 Featured Character fields (must match StoryHybridPanel's FEATURED_FIELDS).
export const FEATURED_FIELD_KEYS = [
  "name_role",
  "industry",
  "appearance",
  "wardrobe",
  "environment",
  "content_problem",
  "consequence",
  "transformation",
];

/**
 * Every precondition the Story-Hybrid render needs, checked against the exact
 * production shapes: series-level `storyConfig` ({ story_format, host:{
 * twin_image_id, voice_id }, featured_character:{...8 fields} }) and the
 * current `episode` ({ storyboard:[...] }).
 *
 * Returns { ok:true } or { ok:false, message } with a SPECIFIC, user-facing
 * reason so a failed precondition is never silent.
 */
export function storyHybridPreflight(storyConfig, episode) {
  const sc = storyConfig || {};
  const host = sc.host || {};
  const featured = sc.featured_character || {};

  if (sc.story_format !== "story_hybrid") {
    return { ok: false, message: "This series isn't set to Story-led Hybrid. Open the Story-led Hybrid panel, switch it on, and Save." };
  }
  if (!host.twin_image_id) {
    return { ok: false, message: "Pick your host AI Twin (e.g. Brittany) in the Story-led Hybrid panel, then Save." };
  }
  if (!host.voice_id) {
    return { ok: false, message: "Select your cloned narrator voice (e.g. bb7/31) in the Story-led Hybrid panel, then Save." };
  }
  const missing = FEATURED_FIELD_KEYS.filter((k) => !String(featured[k] || "").trim());
  if (missing.length) {
    return { ok: false, message: `Fill in all Featured Character fields and Save the story setup first (missing: ${missing.join(", ")}).` };
  }
  if (!(episode && Array.isArray(episode.storyboard) && episode.storyboard.length > 0)) {
    return { ok: false, message: "Generate and save the story storyboard first — click 📋 Storyboard on this episode, then Save Storyboard." };
  }
  return { ok: true, message: null };
}

/**
 * Functionally merge a story-config PATCH into the previous config, preserving
 * every unrelated sibling key. `host` and `featured_character` are deep-merged
 * so a single-field update (e.g. { host: { twin_image_id } }) never clears the
 * other keys (voice_id, story_format, other featured fields).
 */
export function mergeStoryConfig(prev, patch) {
  const p = prev || {};
  const pt = patch || {};
  return {
    ...p,
    ...pt,
    host: pt.host ? { ...(p.host || {}), ...pt.host } : (p.host || {}),
    featured_character: pt.featured_character
      ? { ...(p.featured_character || {}), ...pt.featured_character }
      : (p.featured_character || {}),
  };
}

/**
 * Fire the Story-Hybrid start endpoint EXACTLY ONCE. Returns { taskId, data }
 * or throws an Error whose message is a user-facing reason (server detail when
 * present).
 */
export async function submitStoryHybrid({ API, seriesId, epNum, headers }) {
  let res;
  try {
    res = await axios.post(
      `${API}/series/${seriesId}/episodes/${epNum}/generate-story-hybrid`,
      {},
      { headers }
    );
  } catch (e) {
    const detail = e && e.response && e.response.data && e.response.data.detail;
    throw new Error(detail || (e && e.message) || "Story Hybrid render couldn't start.");
  }
  const taskId = res.data && res.data.task_id;
  if (!taskId) throw new Error("The server didn't return a render job. Please try again.");
  return { taskId, data: res.data };
}


/**
 * Persist a single story-config PATCH with out-of-order protection.
 * `versionRef` is a shared mutable ref ({current:number}); each call takes the
 * next version and applies the server's FULL merged config ONLY if it is still
 * the newest in-flight request. Never throws. Bump `versionRef.current` on any
 * authoritative load/save so a slow stale PATCH response can't overwrite it.
 */
export function persistStoryPatch({ axios: client, API, seriesId, headers, patch, versionRef, setStoryConfig }) {
  const v = ++versionRef.current;
  return client
    .patch(`${API}/series/${seriesId}/story-config`, patch, { headers })
    .then((res) => {
      if (v === versionRef.current && res && res.data) setStoryConfig(res.data);
    })
    .catch(() => {});
}
